// Home cross-module search — desktop-PWA parity for the phone. One bar above the tool
// grid searches the snapshot-warmed lists (tasks · contacts · receipts · minutes · FAQ);
// a result tap opens the record straight in its tool (the phone equivalent of the
// desktop "seed the module's search"). The green plug INSIDE the bar is the Connect
// affordance (org-gated by connect.ts); a typed question fires straight into the chat.
import { showScreen } from "./app";
import { loadSnap } from "./cache";
import { loadContacts, openContactByEmail } from "./contacts";
import { loadFaq, openFaqById } from "./faq";
import { loadTasks, openTaskById } from "./tasks";
import { loadMinutes, openMinuteById } from "./minutes";
import { loadLedger, openLedgerDoc, type LedgerDoc } from "./receipts";
import { loadDocs } from "./docs";
import { loadQuotes } from "./quotes";
import { askConnect, loadHealth, sourceLabel, sourcePopHtml, startersForMode, isModuleMode, type BrainMode } from "./connect";

const $ = <T extends Element = HTMLElement>(sel: string): T | null => document.querySelector<T>(sel);
const esc = (s: string): string => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string));

// Slim views of each module's snapshot — only the fields the search matches/shows.
type STask = { id: string | number; title: string; description?: string | null; status?: string; company_name?: string | null; type_name?: string | null; tags?: string[]; people?: { name?: string | null; email?: string | null }[] };
type SContact = { email: string; name?: string | null; company?: string | null; role?: string | null; domain?: string | null };
type SMinute = { id: string | number; title: string; meeting_date?: string | null; attendees?: { name?: string | null; email?: string }[] };
type SFaq = { id: string | number; question: string; answer: string };

let tasks: STask[] = [];
let contacts: SContact[] = [];
let ledger: LedgerDoc[] = [];
let minutes: SMinute[] = [];
let faqs: SFaq[] = [];

function hydrate(): void {
  tasks = loadSnap<{ tasks?: STask[] }>("tasks")?.tasks || [];
  contacts = loadSnap<SContact[]>("contacts") || [];
  // receipts snapshot = {documents, projects, …} since the ledger editor landed; tolerate the old array shape
  const rc = loadSnap<{ documents?: LedgerDoc[] } | LedgerDoc[]>("receipts");
  ledger = Array.isArray(rc) ? rc : rc?.documents || [];
  minutes = loadSnap<SMinute[]>("minutes") || [];
  faqs = loadSnap<SFaq[]>("faq") || [];
}

// SVG glyphs — the canonical module icons (same paths as the tiles / desktop PWA).
const G = {
  tasks: `<polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>`,
  contacts: `<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><circle cx="18.5" cy="13.5" r="3"/><path d="M23 20.5a3.6 3.6 0 0 0-2.8-2.4"/>`,
  receipts: `<path d="M4 2v20l2-1.5L8 22l2-1.5L12 22l2-1.5L16 22l2-1.5L20 22V2l-2 1.5L16 2l-2 1.5L12 2l-2 1.5L8 2 6 3.5 4 2z"/><path d="M8 8h8M8 12h8M8 16h5"/>`,
  minutes: `<path d="M8 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2"/><rect x="8" y="2" width="8" height="4" rx="1"/><path d="M8 11h8M8 15h6"/>`,
  faq: `<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><path d="M9.1 9a2.5 2.5 0 0 1 4.9.7c0 1.6-2.5 2.3-2.5 2.3"/><path d="M12 16h.01"/>`,
};
const ico = (k: keyof typeof G): string =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${G[k]}</svg>`;

const groupHd = (k: keyof typeof G, label: string): string => `<div class="res-group-hd">${ico(k)} ${label}</div>`;
const row = (go: string, id: string, title: string, sub: string, k: keyof typeof G): string =>
  `<button class="res-row" data-go="${go}" data-id="${esc(id)}">` +
  `<span class="res-ico">${ico(k)}</span>` +
  `<span class="res-body"><span class="res-title">${esc(title)}</span>${sub ? `<span class="res-sub">${esc(sub)}</span>` : ""}</span>` +
  `</button>`;

function render(): void {
  const panel = $("#hsResults");
  const input = $("#hsInput") as HTMLInputElement | null;
  if (!panel || !input) return;
  const needle = input.value.trim().toLowerCase();
  const clear = $("#hsClear") as HTMLElement | null;
  if (clear) clear.hidden = !input.value;
  if (!needle) { panel.classList.remove("open"); panel.innerHTML = ""; return; }

  // Same field-joins and caps as the desktop Home search.
  const tHits = tasks.filter((t) =>
    [t.title, t.description, t.company_name, t.type_name, ...(t.tags || []), ...(t.people || []).map((p) => p.name || p.email || "")]
      .join(" ").toLowerCase().includes(needle)).slice(0, 6);
  const cHits = contacts.filter((c) =>
    `${c.name || ""} ${c.company || ""} ${c.email} ${c.domain || ""}`.toLowerCase().includes(needle)).slice(0, 4);
  const rHits = ledger.filter((d) =>
    `${d.vendor_name || ""} ${d.doc_ref || ""} ${d.file_name || ""} ${d.notes || ""}`.toLowerCase().includes(needle)).slice(0, 4);
  const mHits = minutes.filter((m) =>
    `${m.title} ${(m.attendees || []).map((a) => a.name || a.email).join(" ")}`.toLowerCase().includes(needle)).slice(0, 4);
  const fHits = faqs.filter((f) => `${f.question} ${f.answer}`.toLowerCase().includes(needle)).slice(0, 4);

  let html = "";
  if (tHits.length) html += groupHd("tasks", "Tasks") + tHits.map((t) =>
    row("tasks", String(t.id), t.title, [t.company_name, t.type_name, t.status === "done" ? "Done" : null].filter(Boolean).join(" · "), "tasks")).join("");
  if (cHits.length) html += groupHd("contacts", "Contacts") + cHits.map((c) =>
    row("contacts", c.email, c.name || c.email, [c.company, c.role, c.email].filter(Boolean).join(" · "), "contacts")).join("");
  if (rHits.length) html += groupHd("receipts", "Receipts") + rHits.map((d) =>
    row("receipts", String(d.id), d.vendor_name || d.doc_ref || d.file_name || "Document",
      [d.kind === "invoice" ? "Invoice" : "Receipt", d.doc_date, d.doc_ref].filter(Boolean).join(" · "), "receipts")).join("");
  if (mHits.length) html += groupHd("minutes", "Notes") + mHits.map((m) =>
    row("minutes", String(m.id), m.title,
      [m.meeting_date ? String(m.meeting_date).slice(0, 10) : null, `${(m.attendees || []).length} attendee${(m.attendees || []).length === 1 ? "" : "s"}`].filter(Boolean).join(" · "), "minutes")).join("");
  if (fHits.length) html += groupHd("faq", "FAQ") + fHits.map((f) =>
    row("faq", String(f.id), f.question, f.answer, "faq")).join("");
  if (!html) html = `<div class="res-empty">No matches across tasks, contacts, receipts, notes or FAQ.</div>`;

  panel.innerHTML = html;
  panel.classList.add("open");
}

// ── launch bar (M1, 2026-08-19): the pill row is gone — [✦ Auto ▾] [⚡], mirroring the chat
// bar (Gery: "op de homepage zie je nog de oude chips"). The picked source rides into
// askConnect → Brain opens there, seeded; "auto" is the default policy.
let launchMode: BrainMode | "auto" = "auto";
let selOpen = false; // the Auto ▾ popover on the launch bar
let brainEnabled = false; // org has the Brain module (from rp:connect-meta)
let recipesOpen = false;  // ⚡ options + starters popover, opened IN PLACE above the launch bar
// ⚡ pre-arm toggles (mirror the in-chat ⚡): Deep thinking · Mail me (Chat/modules) · Ask Expert (CRM).
// Reset on a mode-pill switch (each mode opens a fresh launch); they ride into the chat via askConnect.
let lbDeep = false;
let lbMail = false;
let lbAskExpert = false;
// The pre-armed options for THIS mode (mode-appropriate so a stale toggle can't bleed across modes).
function armedOpts(): { deepThinking: boolean; mailMe: boolean; askExpert: boolean } {
  return {
    deepThinking: (launchMode === "auto" || launchMode === "chat" || isModuleMode(launchMode as BrainMode)) && lbDeep,
    mailMe: lbMail,
    askExpert: launchMode === "crm" && lbAskExpert,
  };
}
function renderModes(): void {
  const wrap = $("#hsModes");
  // mic + send (and the pills) show only when the org has Brain — mirrors the desktop launch bar.
  const mic = $("#hsMic") as HTMLElement | null;
  const snd = $("#hsSend") as HTMLElement | null;
  if (mic) mic.style.display = brainEnabled ? "" : "none";
  if (snd) snd.style.display = brainEnabled ? "" : "none";
  if (!wrap) return;
  wrap.style.display = brainEnabled ? "" : "none";
  if (!brainEnabled) { wrap.innerHTML = ""; return; }
  void loadHealth(); // the connector pills' green/red dot (own TTL; re-renders on the rp:connect-health event)
  // ⚡ is never disabled now — every source has options (Auto/Chat → toggles). It lights when armed.
  const lit = lbDeep || (launchMode === "crm" && lbAskExpert);
  // M1 (Gery 2026-08-19): [✦ Auto ▾] [⚡] — the "?" and the pill row are gone; the selector is
  // the same popover as the chat bar (sourcePopHtml), opening upward above the search bar.
  wrap.innerHTML =
    `<div class="brain-selwrap">` +
      `<button class="brain-selector${launchMode === "auto" ? " auto" : ""}" data-seltoggle aria-haspopup="listbox" aria-expanded="${selOpen}" title="Choose where your question is answered from">${esc(sourceLabel(launchMode))} <span class="brain-sel-car">▾</span></button>` +
      (selOpen ? sourcePopHtml(launchMode) : "") +
    `</div>` +
    `<button class="brain-mode zap${recipesOpen ? " on" : ""}${lit ? " lit" : ""}" data-zap title="Chat options" aria-label="Chat options">⚡</button>`;
}

// ⚡ options + starters popover — opens IN PLACE above the launch bar (mirrors the chat's ⚡).
// The options block uses the SAME .chat-opts/.chat-opt-row/.cn-switch markup as the in-chat ⚡,
// but reflecting the launch-bar's own lbDeep/lbMail/lbAskExpert (NOT the connect.ts globals) so the
// chosen options pre-arm the first launched turn. CRM → Ask Expert; every other mode → Deep thinking.
function lbOptsHtml(): string {
  if (launchMode === "crm") {
    return `<div class="chat-opts"><div class="chat-opts-h">Ask Expert</div>` +
      `<div class="chat-opt-row" data-opt="arm"><div class="co-l"><div class="co-name">Ask Expert (deep analysis)</div><div class="co-sub">Run the deep analyst pass on every answer · slower · more credits</div></div><span class="cn-switch${lbAskExpert ? " on" : ""}"></span></div>` +
      `<div class="chat-opt-row" data-opt="mail"><div class="co-l"><div class="co-name">Mail me the result</div><div class="co-sub">Ask &amp; walk away — the answer arrives by email</div></div><span class="cn-switch${lbMail ? " on" : ""}"></span></div>` +
      `</div>`;
  }
  return `<div class="chat-opts"><div class="chat-opts-h">Chat options</div>` +
    `<div class="chat-opt-row" data-opt="deep"><div class="co-l"><div class="co-name">Deep thinking</div><div class="co-sub">Strongest model · shows its thinking · costs more</div></div><span class="cn-switch${lbDeep ? " on" : ""}"></span></div>` +
    `<div class="chat-opt-row" data-opt="mail"><div class="co-l"><div class="co-name">Mail me the result</div><div class="co-sub">Ask &amp; walk away — the answer arrives by email</div></div><span class="cn-switch${lbMail ? " on" : ""}"></span></div>` +
    `</div>`;
}
function renderRecipes(): void {
  const wrap = $("#hsRecipes");
  if (!wrap) return;
  if (!recipesOpen) { wrap.classList.remove("open"); wrap.innerHTML = ""; return; }
  // Always paint when open — Auto/Chat have options even with no starters.
  const recs = startersForMode(launchMode === "auto" ? "chat" : launchMode);
  wrap.innerHTML = lbOptsHtml() + recs.map((r) => `<button class="hr-row" data-prompt="${esc(r.prompt)}">${esc(r.title)}</button>`).join("");
  wrap.classList.add("open");
}

function close(clearText = false): void {
  const input = $("#hsInput") as HTMLInputElement | null;
  if (clearText && input) input.value = "";
  const clear = $("#hsClear") as HTMLElement | null;
  if (clear) clear.hidden = !(input && input.value);
  const panel = $("#hsResults");
  panel?.classList.remove("open");
  if (panel) panel.innerHTML = "";
  if (recipesOpen || selOpen) { recipesOpen = false; selOpen = false; renderModes(); renderRecipes(); }
}

// Result tap → open the record in its tool. Each loadX() paints its snapshot
// synchronously, so the immediate openX() finds the row; the fetch revalidates behind it.
function go(target: string, id: string): void {
  close(true);
  if (target === "tasks") { showScreen("tasks"); void loadTasks(); openTaskById(id); }
  else if (target === "contacts") { showScreen("contacts"); void loadContacts(); openContactByEmail(id); }
  else if (target === "receipts") { showScreen("receipts"); void loadLedger(); openLedgerDoc(id); }
  else if (target === "minutes") { showScreen("minutes"); void loadMinutes(); openMinuteById(id); }
  else if (target === "faq") { showScreen("faq"); void loadFaq(); openFaqById(id); }
}

// ── boot prefetch ──────────────────────────────────────────────────────────
// Desktop parity: the PWA prefetches every module on app start so Home badges and
// search are warm. Staggered so nothing competes with the first paint; gated on a
// linked session (no 401 spam on the pairing screen). Each load also re-saves its
// snapshot → cache.ts fires rp:snap → badges/search re-hydrate.
let prefetched = false;
export function prefetchData(): void {
  if (prefetched || !loadSnap("session")) return;
  prefetched = true;
  setTimeout(() => void loadTasks(), 500);
  setTimeout(() => void loadContacts(), 1100);
  setTimeout(() => void loadMinutes(), 1700);
  setTimeout(() => void loadFaq(), 2300);
  setTimeout(() => void loadLedger(), 2900);
  setTimeout(() => void loadDocs(), 3500);
  setTimeout(() => void loadQuotes(), 4100); // also reveals the gated Quotes tile for a quotes-enabled org
}

export function initHomeSearch(): void {
  hydrate();
  document.addEventListener("rp:snap", hydrate);

  const input = $("#hsInput") as HTMLInputElement | null;
  input?.addEventListener("input", render);
  input?.addEventListener("focus", () => { hydrate(); if (input.value.trim()) render(); });
  input?.addEventListener("keydown", (e) => {
    if (e.key === "Escape") close(true);
    if (e.key === "Enter" && brainEnabled && input.value.trim()) { // Enter = ask Brain in the picked mode + armed ⚡ options
      const q = input.value;
      close(true);
      askConnect(q, launchMode, armedOpts());
    }
  });
  $("#hsClear")?.addEventListener("click", () => { close(true); input?.focus(); });
  // send (▶) → ask Brain in the picked mode with the typed text + armed ⚡ options; mic → open + start voice
  $("#hsSend")?.addEventListener("click", () => { const q = input?.value || ""; close(true); askConnect(q, launchMode, armedOpts()); });
  $("#hsMic")?.addEventListener("click", () => { const q = input?.value || ""; close(true); askConnect(q, launchMode, { voice: true, ...armedOpts() }); });
  // launch bar: the Auto ▾ selector picks the launch source; ⚡ → options + starters popover IN PLACE
  $("#hsModes")?.addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    const so = t.closest<HTMLElement>("[data-selopt]");
    if (so) {
      const v = so.getAttribute("data-selopt") || "auto";
      launchMode = v === "auto" ? "auto" : (v as BrainMode);
      selOpen = false; recipesOpen = false; lbDeep = false; lbMail = false; lbAskExpert = false;
      renderModes(); renderRecipes();
      return;
    }
    if (t.closest<HTMLElement>("[data-seltoggle]")) { selOpen = !selOpen; renderModes(); return; }
    const btn = t.closest<HTMLElement>(".brain-mode");
    if (!btn) return;
    if (btn.hasAttribute("data-zap")) { recipesOpen = !recipesOpen; renderModes(); renderRecipes(); return; }
  });
  // popover clicks: a ⚡ option toggle re-paints in place; a starter opens Brain + runs the recipe immediately
  $("#hsRecipes")?.addEventListener("click", (e) => {
    const opt = (e.target as HTMLElement).closest<HTMLElement>("[data-opt]");
    if (opt) {
      const which = opt.getAttribute("data-opt");
      if (which === "deep") lbDeep = !lbDeep;
      else if (which === "mail") lbMail = !lbMail;
      else if (which === "arm") lbAskExpert = !lbAskExpert;
      renderModes(); // ⚡ "lit" state may have changed
      renderRecipes(); // reflect the toggle
      return;
    }
    const r = (e.target as HTMLElement).closest<HTMLElement>(".hr-row");
    if (!r) return;
    const prompt = r.getAttribute("data-prompt") || "";
    close(true);
    askConnect(prompt, launchMode, armedOpts()); // open Brain + run the recipe immediately
  });
  document.addEventListener("rp:connect-meta", (e) => { brainEnabled = !!(e as CustomEvent).detail; renderModes(); });
  document.addEventListener("rp:connect-health", () => renderModes()); // dots landed → repaint the pills
  renderModes();
  $("#hsResults")?.addEventListener("click", (e) => {
    const r = (e.target as HTMLElement).closest<HTMLElement>("[data-go]");
    if (r) go(r.getAttribute("data-go")!, r.getAttribute("data-id")!);
  });
  // outside tap closes the panel (house dropdown behaviour)
  document.addEventListener("pointerdown", (e) => {
    const t = e.target as Element | null;
    if (t?.closest?.("#hsWrap")) return;
    close();
  });
}
